/**
 * Integrytas Group - Base App
 * Root component for the MOBILE build. Mirrors App.tsx's authentication flow
 * (reusing the same useAuthSession hook and auth screens) but renders the
 * mobile shell instead of the desktop TabManagerShell. The authenticated view
 * resolves pages from the SEPARATE mobile registry (MOBILE_PAGE_COMPONENTS) and
 * the same RBAC-filtered menu — sharing the data/logic layer (apiDb, Common
 * Routine, RLS, page_registry) while the presentation is mobile-native (§16).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthSession } from '../lib/use_auth_session';
import { useActiveLocale } from '../lib/use_active_locale';
import { usePageSync } from '../lib/use_page_sync';
import { loadPageLabels } from '../lib/auth_loader';
import { SignInScreen } from '../components/auth/SignInScreen';
import { RegisterScreen } from '../components/auth/RegisterScreen';
import { ForgotPasswordScreen } from '../components/auth/ForgotPasswordScreen';
import { SetNewPasswordScreen } from '../components/auth/SetNewPasswordScreen';
import { FirstAdminSetupScreen } from '../components/auth/FirstAdminSetupScreen';
import { ChangePasswordModal } from '../components/auth/ChangePasswordModal';
import { Modal } from '../components/common/Modal';
import { Watermark } from '../components/common/Watermark';
import { load_menu_entitlement, filter_menu_by_entitlement, type menu_entitlement } from '../lib/license_menu';
import { MOBILE_PAGE_COMPONENTS } from './mobile_page_components';
import { MobileScaffoldPage } from './pages/MobileScaffoldPage';
import { ApprovalPage } from '../pages/approval/ApprovalPage';
import { PortalLoginPage } from '../pages/portal/PortalLoginPage';
import { APPLICATION_NAME, BRANDING_SUFFIX } from '../constants/branding';
import { setActiveCsmLocale, loadI18nOverrides } from '../i18n';
import { MobileShell } from './MobileShell';
import type { OpenTab } from '../components/layout/TabManagerShell';

type AuthView = 'sign_in' | 'register' | 'forgot_password';

export default function MobileApp() {
  // Public approval magic-link short-circuit (parity with the desktop app).
  if (typeof window !== 'undefined' && window.location.pathname === '/approve') {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const action = params.get('action');
    if (token) return <ApprovalPage token={token} action={action} />;
  }
  if (typeof window !== 'undefined' && window.location.pathname === '/portal') {
    return <PortalLoginPage />;
  }

  const { t } = useTranslation();
  const auth = useAuthSession();
  const activeLocale = useActiveLocale();
  const sync = usePageSync(auth.session);

  const [pageLabels, setPageLabels] = useState<Record<string, string>>({});
  const [menuEnt, setMenuEnt] = useState<menu_entitlement | null>(null);
  const [authView, setAuthView] = useState<AuthView>('sign_in');
  const [isFirstRun, setFirstRun] = useState<boolean | null>(null);
  const [changePasswordOpen, setCpOpen] = useState(false);
  const [mimicTarget, setMimicTarget] = useState<number | null>(null);
  const [mimicReason, setMimicReason] = useState('');
  const [mimicError, setMimicError] = useState<string | null>(null);
  const [mimicBusy, setMimicBusy] = useState(false);

  useEffect(() => {
    if (auth.status !== 'unauthenticated') return;
    let cancelled = false;
    void auth.checkFirstRun().then((v) => { if (!cancelled) setFirstRun(v); });
    return () => { cancelled = true; };
  }, [auth.status, auth]);

  useEffect(() => {
    if (auth.status !== 'authenticated' || !auth.session) return;
    const ids = auth.session.effective_user.page_access.map((p) => p.page_id);
    void loadPageLabels(ids, activeLocale)
      .then(setPageLabels)
      .catch(() => setPageLabels({}));
  }, [auth.status, auth.session, sync.ran, sync.inserted, sync.updated, activeLocale]);

  useEffect(() => {
    if (auth.status !== 'authenticated' || !auth.session) { setMenuEnt(null); return; }
    let cancelled = false;
    void load_menu_entitlement()
      .then((e) => { if (!cancelled) setMenuEnt(e); })
      .catch(() => { if (!cancelled) setMenuEnt(null); });
    return () => { cancelled = true; };
  }, [auth.status, auth.session]);

  useEffect(() => {
    const locale =
      auth.status === 'authenticated' && auth.session
        ? auth.session.effective_user.preferred_locale
        : 'EN_US';
    setActiveCsmLocale(locale);
    if (auth.status === 'authenticated') void loadI18nOverrides(locale);
  }, [auth.status, auth.session]);

  const licensedMenu = useMemo(() => {
    const pa = auth.session?.effective_user.page_access ?? [];
    return menuEnt ? filter_menu_by_entitlement(pa, menuEnt) : pa;
  }, [auth.session, menuEnt]);

  // Password recovery must sit above the authenticated branch.
  if (auth.isPasswordRecovery) {
    return (
      <SetNewPasswordScreen
        onSubmit={auth.completePasswordRecovery}
        onCancel={auth.signOut}
        errorMessage={auth.error}
      />
    );
  }

  if (auth.status === 'loading') {
    return (
      <div className="m-center">
        <Watermark appName={APPLICATION_NAME} brandingSuffix={BRANDING_SUFFIX} />
        Loading session...
      </div>
    );
  }

  if (auth.status !== 'authenticated' || !auth.session) {
    if (isFirstRun) {
      return <FirstAdminSetupScreen onCreateAdmin={auth.signUp} errorMessage={auth.error} />;
    }
    if (authView === 'register') {
      return (
        <RegisterScreen
          onRegister={auth.signUp}
          onBackToSignIn={() => setAuthView('sign_in')}
          errorMessage={auth.error}
        />
      );
    }
    if (authView === 'forgot_password') {
      return (
        <ForgotPasswordScreen
          onRequestReset={auth.requestPasswordReset}
          onBackToSignIn={() => setAuthView('sign_in')}
          errorMessage={auth.error}
        />
      );
    }
    return (
      <SignInScreen
        onSignIn={auth.signIn}
        onDevLogin={auth.devLogin}
        onRequestRegister={() => setAuthView('register')}
        onRequestForgot={() => setAuthView('forgot_password')}
        errorMessage={auth.error}
      />
    );
  }

  const session = auth.session;

  const handleBeginMimic = (userId: number) => {
    setMimicReason('');
    setMimicError(null);
    setMimicTarget(userId);
  };
  const confirmMimic = async () => {
    if (mimicTarget == null) return;
    if (mimicReason.trim().length < 5) {
      setMimicError('A business reason of at least 5 characters is required.');
      return;
    }
    setMimicBusy(true);
    setMimicError(null);
    try {
      await auth.beginMimic(mimicTarget, mimicReason.trim());
      setMimicTarget(null);
    } catch (e) {
      setMimicError((e as Error).message);
    } finally {
      setMimicBusy(false);
    }
  };

  const renderPage = (tab: OpenTab) => {
    // §16: resolve mobile-native screens from the SEPARATE mobile registry — never
    // the desktop PAGE_COMPONENTS. A code with no bespoke mobile screen yet renders
    // the generic scaffold, so every page is routable on mobile (the desktop_only
    // flag, not the presence of a screen, decides whether a page opens at all).
    const Component = MOBILE_PAGE_COMPONENTS[tab.page_internal_code];
    if (Component) {
      return <Component session={session} onBeginMimic={handleBeginMimic} />;
    }
    return <MobileScaffoldPage code={tab.page_internal_code} title={tab.display_text} route={tab.route_path} />;
  };

  return (
    <>
      <MobileShell
        session={session}
        menu={licensedMenu}
        pageLabels={pageLabels}
        onSignOut={() => { void auth.signOut(); }}
        onEndMimic={() => { void auth.endMimic(); }}
        onChangePassword={() => setCpOpen(true)}
        renderPage={renderPage}
      />
      <ChangePasswordModal
        open={changePasswordOpen}
        onClose={() => setCpOpen(false)}
        onSubmit={auth.changePassword}
      />
      {mimicTarget != null && (
        <Modal
          title={t('mobile.mimic_title', 'Begin mimic session')}
          width="md"
          onClose={() => { if (!mimicBusy) setMimicTarget(null); }}
          footer={
            <>
              <button onClick={() => setMimicTarget(null)} disabled={mimicBusy}
                className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft disabled:opacity-60">Cancel</button>
              <button onClick={() => void confirmMimic()} disabled={mimicBusy}
                className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">
                {mimicBusy ? 'Starting…' : 'Begin mimic'}
              </button>
            </>
          }
        >
          <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">Business reason</label>
          <textarea autoFocus value={mimicReason} onChange={(e) => setMimicReason(e.target.value)} rows={3}
            placeholder={t('mobile.mimic_reason_ph', 'Why are you impersonating this user? (recorded against the real admin)')}
            className="w-full rounded border border-csm-border px-2 py-1 text-csm-sm focus:border-primary focus:outline-none" />
          <p className="mt-1 text-csm-2xs text-csm-watermark">All actions during the session are stamped with your real admin identity.</p>
          {mimicError && (
            <div className="mt-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">{mimicError}</div>
          )}
        </Modal>
      )}
    </>
  );
}
